import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { X, Search, ExternalLink, Check } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import './WorkspaceHub.css';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const STATUS_LABELS = {
    active: 'Active',
    available: 'Available to install',
    'coming-soon': 'Coming soon',
};

const HEALTH_LABELS = {
    healthy: 'Operational',
    degraded: 'Degraded',
    down: 'Down',
    unknown: 'Status unknown',
};

export default function WorkspaceHub({ onClose, isInstalled, getHealthStatus }) {
    const { getAuthHeader } = useAuth();
    const { currentWorkspace, setCurrentWorkspace, visibleWorkspaces, isWorkspaceActive, loading: providersLoading } = useWorkspace();
    const [query, setQuery] = useState('');
    const [requested, setRequested] = useState(() => new Set());
    const [requestingId, setRequestingId] = useState(null);
    const searchRef = useRef(null);
    const dialogRef = useRef(null);
    const previouslyFocused = useRef(null);

    useEffect(() => {
        previouslyFocused.current = document.activeElement;
        // Focus the search input on open
        searchRef.current?.focus();
        return () => {
            // Restore focus to whatever triggered the hub (the rail button)
            if (previouslyFocused.current && typeof previouslyFocused.current.focus === 'function') {
                previouslyFocused.current.focus();
            }
        };
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
            if (e.key === 'Tab' && dialogRef.current) {
                // Simple focus trap
                const focusables = dialogRef.current.querySelectorAll(
                    'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                if (!focusables.length) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return visibleWorkspaces;
        return visibleWorkspaces.filter(ws => ws.name.toLowerCase().includes(q));
    }, [visibleWorkspaces, query]);

    // Settings and Admin are virtual workspaces accessed via dedicated rail
    // buttons (gear + shield) — they don't belong in the hub's "Pinned to rail"
    // group, which is meant for switching between real workspaces.
    const pinned = filtered.filter(w => isWorkspaceActive(w) && w.id !== 'settings' && w.id !== 'admin');
    const available = filtered.filter(w => w.status === 'available' && !isWorkspaceActive(w));
    const comingSoon = filtered.filter(w => w.status === 'coming-soon');

    const handleSelect = useCallback((ws) => {
        if (!isWorkspaceActive(ws)) return;
        if (ws.type === 'module-iframe' && ws.providerId && typeof isInstalled === 'function' && !isInstalled(ws.providerId)) {
            toast.warning(`${ws.name} is not installed. Contact your administrator to enable this module.`);
            return;
        }
        setCurrentWorkspace(ws.id);
        onClose();
    }, [isInstalled, isWorkspaceActive, setCurrentWorkspace, onClose]);

    const requestInstall = useCallback(async (ws) => {
        if (requested.has(ws.id) || requestingId) return;
        setRequestingId(ws.id);
        try {
            await axios.post(`${API}/feature-requests/`, {
                title: `Module install request: ${ws.name}`,
                description: 'Requested from the Workspace Hub by a team member.',
                type: 'feature',
                category: 'other',
            }, { headers: getAuthHeader() });
            setRequested(prev => new Set(prev).add(ws.id));
            toast.success(`${ws.name} install requested. Admins have been notified.`);
        } catch (err) {
            toast.error(`Could not send request for ${ws.name}. Please try again.`);
        } finally {
            setRequestingId(null);
        }
    }, [getAuthHeader, requested, requestingId]);

    return (
        <div className="hub-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div
                ref={dialogRef}
                className="hub-panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="hub-title"
            >
                <header className="hub-header">
                    <div>
                        <h2 id="hub-title" className="hub-title">Workspace Hub</h2>
                        <p className="hub-sub">Switch, install, or preview modules.</p>
                    </div>
                    <button
                        type="button"
                        className="hub-close"
                        onClick={onClose}
                        aria-label="Close Workspace Hub"
                    >
                        <X size={16} />
                    </button>
                </header>

                <div className="hub-searchwrap">
                    <Search size={14} aria-hidden="true" />
                    <input
                        ref={searchRef}
                        type="text"
                        className="hub-search"
                        placeholder="Search workspaces…"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        aria-label="Search workspaces"
                    />
                </div>

                <div className="hub-body">
                    {pinned.length > 0 && (
                        <HubGroup label="Pinned to rail">
                            {pinned.map(ws => (
                                <HubCard
                                    key={ws.id}
                                    ws={ws}
                                    isCurrent={ws.id === currentWorkspace.id}
                                    health={ws.providerId && typeof getHealthStatus === 'function' ? getHealthStatus(ws.providerId) : 'unknown'}
                                    onClick={() => handleSelect(ws)}
                                />
                            ))}
                        </HubGroup>
                    )}

                    {available.length > 0 && !providersLoading && (
                        <HubGroup label="Available to install">
                            {available.map(ws => (
                                <HubAvailableCard
                                    key={ws.id}
                                    ws={ws}
                                    requested={requested.has(ws.id)}
                                    requesting={requestingId === ws.id}
                                    onRequest={() => requestInstall(ws)}
                                />
                            ))}
                        </HubGroup>
                    )}

                    {comingSoon.length > 0 && (
                        <HubGroup label="Coming soon">
                            {comingSoon.map(ws => (
                                <HubSoonCard key={ws.id} ws={ws} />
                            ))}
                        </HubGroup>
                    )}

                    {filtered.length === 0 && (
                        <div className="hub-empty">No workspaces match “{query}”.</div>
                    )}
                </div>

                <footer
                    className="hub-footer"
                    onClick={() => {
                        onClose();
                        toast.info('Module Marketplace — coming soon');
                    }}
                >
                    <ExternalLink size={14} aria-hidden="true" />
                    <span>Browse Module Marketplace</span>
                </footer>
            </div>
        </div>
    );
}

function HubGroup({ label, children }) {
    return (
        <section className="hub-group">
            <div className="hub-group-label">{label}</div>
            <div className="hub-grid">{children}</div>
        </section>
    );
}

function GlyphTile({ ws }) {
    const Glyph = ws.glyph;
    return (
        <div className="hub-tile" style={{ background: ws.hue }} aria-hidden="true">
            <Glyph size={16} strokeWidth={2} color="#fff" />
        </div>
    );
}

function HubCard({ ws, isCurrent, health, onClick }) {
    return (
        <button
            type="button"
            className={`hub-card ${isCurrent ? 'current' : ''}`}
            onClick={onClick}
            style={isCurrent ? { '--ws-current': ws.hue } : undefined}
            data-testid={`workspace-card-${ws.id}`}
            aria-label={`${ws.name} — ${STATUS_LABELS[ws.status]}`}
        >
            <GlyphTile ws={ws} />
            <div className="hub-card-info">
                <div className="hub-card-name">{ws.name}</div>
                <div className="hub-card-status">
                    <span className={`hub-dot ${health}`} aria-hidden="true" />
                    <span>{HEALTH_LABELS[health] || HEALTH_LABELS.unknown}</span>
                    <span className="hub-card-type">· {ws.type === 'module-iframe' ? 'module' : 'native'}</span>
                </div>
            </div>
        </button>
    );
}

function HubAvailableCard({ ws, requested, requesting, onRequest }) {
    return (
        <div className="hub-card available" data-testid={`workspace-card-${ws.id}`}>
            <GlyphTile ws={ws} />
            <div className="hub-card-info">
                <div className="hub-card-name">{ws.name}</div>
                <div className="hub-card-status">
                    <span className="hub-dot available" aria-hidden="true" />
                    <span>{STATUS_LABELS[ws.status]}</span>
                </div>
            </div>
            <button
                type="button"
                className="hub-install-btn"
                onClick={onRequest}
                disabled={requested || requesting}
                data-testid={`hub-install-${ws.id}`}
            >
                {requested ? (<><Check size={14} /> Requested</>) : requesting ? 'Requesting…' : 'Request install'}
            </button>
        </div>
    );
}

function HubSoonCard({ ws }) {
    return (
        <div className="hub-card soon" data-testid={`workspace-card-${ws.id}`}>
            <GlyphTile ws={ws} />
            <div className="hub-card-info">
                <div className="hub-card-name">{ws.name}</div>
                <div className="hub-card-status">
                    <span className="hub-dot soon" aria-hidden="true" />
                    <span>{STATUS_LABELS[ws.status]}</span>
                </div>
            </div>
        </div>
    );
}
